import React, { useEffect, useLayoutEffect, useRef } from "react";

import { emphasize } from "@mui/material/styles";
import * as echarts from "echarts";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material";

const emptyColor = "#DADADA";

export const chartColor = (baseColor, count, index) => {
    if (isNaN(count) || count < 1) {
        return baseColor;
    }
    return emphasize(baseColor, (0.8 / count) * index);
};

export const ReactECharts = ({ option, minWidth, minHeight, onClick }) => {
    const containerRef = useRef(),
        chartRef = useRef();
    // const [size, setSize] = useState({});
    useLayoutEffect(() => {
        var myChart = echarts.init(containerRef.current, null, { renderer: "svg" });
        /*
        grid: {
            top:    60,
            bottom: 60,
            left:   '10%',
            right:  '10%',
          },
          */
        myChart.setOption(option);

        myChart.on("click", function (event) {
            !!onClick && onClick(event);
        });

        chartRef.current = myChart;

        let observer = new ResizeObserver((a) => {
            let width = a[0].contentRect.width,
                height = a[0].contentRect.height;

            //chartRef.current.dispose(containerRef.current);

            //chartRef.current = echarts.init(containerRef.current, null, { renderer: "svg" });
            //chartRef.current.setOption({ width, height, ...option });

            myChart.resize({ width, height });

            //setSize({ width, height });
        });
        observer.observe(containerRef.current);
        return () => {
            myChart.dispose();
            observer.disconnect(containerRef.current);
        };
    }, []);

    useEffect(() => {
        if (chartRef.current) {
            chartRef.current.setOption(option);
        }
    }, [option]);

    return (
        <div
            style={{
                position: "relative",
                height: "100%",
                width: "100%",
                minHeight: minHeight || 140,
                minWidth: minWidth || 140,
                overflow: "hidden",
                //border: "1px solid red",
            }}>
            <div
                style={{ position: "absolute", width: "100%", height: "100%" }}
                ref={containerRef}></div>
        </div>
    );
};

const emptyDataPie = {
    label: "empty",
    data: [1],
    backgroundColor: [emptyColor],
    hoverBackgroundColor: [emptyColor],
};

export const DonutChart = ({
    data,
    children,
    colors,
    dataColors,
    centerTitle,
    centerValue,
    disabled,
    useChartColors = true,
    size = 300,
    onClick,
    radius = 95,
}) => {
    const series = [];
    const hasData = Array.isArray(data) && data.length;

    if (!Array.isArray(colors) || !colors.length) {
        //TODO populate chart colors
        colors = [];
        let max = Math.max((data || []).length, 1);
        for (let i = 0; i < max; i++) {
            colors.push(chartColor("#6A6A6A", max, i));
        }
    }

    if (hasData) {
        for (let i = 0; i < data.length; i++) {
            //check if data
            let val = 0;
            if (Array.isArray(data[i]) && data[i].length) {
                for (let j = 0; j < data[i].length; j++) {
                    val += (data[i][j] || 0) * 1;
                }
            }

            if (!val || isNaN(val) || val === 0) {
                series.push({
                    type: "pie",
                    label: { show: false },
                    data: [{ value: 1, itemStyle: { color: emptyColor } }],
                });
                continue;
            }

            let s = { type: "pie", label: { show: false }, data: [] };
            for (let j = 0; j < data[i].length; j++) {
                s.data.push({
                    value: data[i][j] || 0,
                    itemStyle: {
                        color: useChartColors
                            ? chartColor(colors[i % colors.length], data[i].length, j)
                            : colors[j % colors.length],
                    },
                });
            }
            series.push(s);
        }
    } else {
        series.push({
            type: "pie",
            label: { show: false },
            data: [{ value: 1, itemStyle: { color: emptyColor } }],
        });
    }

    for (let i = 0; i < series.length; i++) {
        series[i].startAngle = 270;
        series[i].radius = [`${radius - 15 - i * 17}%`, `${radius - i * 17}%`];
    }

    let options = {
        series,
        width: "auto",
        height: "auto",
    };

    if (centerValue) {
        options.title = {
            text: `${centerValue}`,
            left: "center",
            top: "center",
        };
    }

    return <ReactECharts option={options} minHeight={size} onClick={onClick} />;
};

export const BarChart = ({
    data,
    disabled,
    orientation = "horizontal",
    formatLabel,
    formatValue,
    formatName,
    onClick,
    color,
}) => {
    const theme = useTheme();
    const isHorizontal = !orientation || orientation === "horizontal";
    if (!Array.isArray(data)) {
        data = [];
    }

    console.log(data);

    return (
        <ReactECharts
            option={{
                width: "auto",
                height: "auto",
                maintainAspectRatio: false,
                [isHorizontal ? "yAxis" : "xAxis"]: {
                    type: "category",
                    data: data.map(formatName || ((nv) => nv.name)),
                    axisLine: { show: false },
                    axisTick: {
                        show: false,
                    },
                    axisLabel: {
                        inerval: 0,
                        formatter: formatLabel || ((v) => v.value),
                        rotate: isHorizontal ? 0 : 90,
                    },
                },
                [isHorizontal ? "xAxis" : "yAxis"]: {
                    show: false,
                    type: "value",
                },
                grid: {
                    containLabel: true,
                    left: "5%",
                    top: isHorizontal ? "5%" : "10%",
                    right: isHorizontal ? "25%" : "5%",
                    bottom: "5%",
                },
                series: [
                    {
                        type: "bar",
                        label: {
                            show: true,
                            position: isHorizontal ? "right" : "top",
                            formatter: formatValue || ((v) => v.value),
                        },
                        labelLine: { show: false },
                        //labelLayout: { hideOverlap: true },
                        avoidLabelOverlap: true,
                        containLabel: true,
                        data: data.map((nv) => ({
                            value: nv.value,
                            payload: nv,
                            itemStyle: {
                                color: disabled
                                    ? theme.palette.outlineVariant.primary
                                    : nv.color || color || theme.palette.primary.main,

                                borderRadius: [10, 10, 0, 0],
                            },
                        })),
                    },
                ],
            }}
            onClick={onClick}
        />
    );
};

const ChartLabel = ({ color, label, value }) => {
    return (
        <Box
            sx={(theme) => ({
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                alignItems: "center",
                gap: theme.spacing(1),
            })}>
            <Box sx={{ flex: 0 }}>
                <Box
                    sx={{
                        display: "inline-block",
                        width: "0.75em",
                        height: "0.75em",
                        borderRadius: "0.75em",
                        backgroundColor: color,
                    }}></Box>
            </Box>
            <Box
                sx={{
                    flex: 1,
                    "& > *": {
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    },
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    minWidth: 0,
                }}>
                <Typography variant="caption">{label}</Typography>
            </Box>
        </Box>
    );
};

export const ChartLabels = ({ labels, color }) => {
    if (!Array.isArray(labels)) {
        return null;
    }
    /*
           let max = Math.max((data || []).length, 1);
        for (let i = 0; i < max; i++) {
            colors.push(chartColor("#6A6A6A", max, i));
            */
    return (
        <Box>
            {labels.map((l, idx) => (
                <ChartLabel
                    label={l.name}
                    color={l.color || chartColor(color, labels.length, idx)}
                    value={l.value}
                    key={idx}
                />
            ))}
        </Box>
    );
};
